/**
 * The view of a BI dashboard ↔ the URL (CU-869fmhmx8 D8, CA 7). A relative range travels as its key, so a copied
 * link opened another day moves with it; a fixed range travels with its dates.
 */
import { fromIso, toIso } from './dates.js'
import { DEFAULT_RANGE, RANGES, isRangeKey, isValidFixed } from './ranges.js'
import { CARS, NO_CARS, parseCars } from './cars.js'
import { flag, idList, positiveInt } from './params.js'

export const COMPARE = Object.freeze(['ly', 'lw', 'yday'])
export const SHOW_BY = Object.freeze(['week', 'day'])
export const STATS = Object.freeze(['min', 'avg', 'max'])

// CA 6: last year, same weekday, by week, Min, 7-day rentals, current and next month
export const DEFAULT_VIEW = Object.freeze({
	office: null, lor: 7, range: DEFAULT_RANGE, from: null, to: null,
	cmp: 'ly', same: false, by: 'week', stat: 'min', prev: false,
	// filters (E4): cars before the load; tier or providers after it. Never the search
	cars: CARS.ALL, carsIds: [], tier: null, providers: [],
})

/**
 * Only the valid keys of a URL (or of a stored view): `{ office?, lor?, range?, from?, to?, cmp?, same?, by?, stat?,
 * prev?, cars?, carsIds?, tier?, providers? }`. A whole view (it has `range`) says its filters even when it has none,
 * so a copied link without filters opens without them; a partial one (`?office=70`) leaves them as they were.
 */
export function parseView(params) {
	const get = k => (typeof params?.get === 'function' ? params.get(k) : params?.[k])
	const out = {}

	const office = positiveInt(get('office'))
	if (office) out.office = office
	const lor = positiveInt(get('lor'))
	if (lor) out.lor = lor

	const range = get('range')
	if (range === 'fixed') {
		const from = fromIso(get('from')), to = fromIso(get('to'))
		if (isValidFixed({ from, to })) Object.assign(out, { range, from: toIso(from), to: toIso(to) })
	} else if (isRangeKey(range)) {
		out.range = range
	}

	if (COMPARE.includes(get('cmp'))) out.cmp = get('cmp')
	if (SHOW_BY.includes(get('by'))) out.by = get('by')
	if (STATS.includes(get('stat'))) out.stat = get('stat')
	for (const k of ['same', 'prev']) {
		const v = flag(get(k))
		if (v !== null) out[k] = v
	}

	const whole = !!get('range')
	if (get('cars') || whole) Object.assign(out, get('cars') ? parseCars(get('cars'), get('carsIds')) : { ...NO_CARS, carsIds: [] })
	// a tier, or the providers picked by hand: touching Providers drops the tier (E2)
	const tier = positiveInt(get('tier'))
	const providers = idList(get('providers'))
	if (tier) Object.assign(out, { tier, providers: [] })
	else if (providers) Object.assign(out, { tier: null, providers })
	else if (whole) Object.assign(out, { tier: null, providers: [] })
	return out
}

/** The view, first what the URL says, then what was stored, then the defaults */
export function resolveView(...sources) {
	const view = { ...DEFAULT_VIEW }
	for (const source of [...sources].reverse()) Object.assign(view, parseView(source || {}))
	if (view.range !== 'fixed') Object.assign(view, { from: null, to: null })
	return view
}

/** View → query string. A relative range never carries dates. */
export function viewToParams(view) {
	const p = new URLSearchParams()
	if (view.office) p.set('office', String(view.office))
	if (view.lor) p.set('lor', String(view.lor))
	p.set('range', view.range)
	if (view.range === 'fixed' && view.from && view.to) {
		p.set('from', view.from)
		p.set('to', view.to)
	}
	p.set('cmp', view.cmp)
	if (view.cmp === 'ly') p.set('same', view.same ? '1' : '0')
	p.set('by', view.by)
	p.set('stat', view.stat)
	p.set('prev', view.prev ? '1' : '0')
	if (view.cars && view.cars !== CARS.ALL && view.carsIds?.length) {
		p.set('cars', view.cars)
		p.set('carsIds', view.carsIds.join(','))
	}
	if (view.tier) p.set('tier', String(view.tier))
	else if (view.providers?.length) p.set('providers', view.providers.join(','))
	return p.toString()
}

/** The plain object stored per user, to reopen the last view */
export const viewToStored = view => Object.fromEntries(new URLSearchParams(viewToParams(view)))

export const storageKey = uid => `rentacar.dashboards.seasonVsLastYear.${uid || 'anon'}`

/**
 * Changing "Compare with" (UX Obs. 3, Curro's decision 4):
 *  - to Last week / Yesterday: by day; with the range untouched on "Current and next month", "Next 4 weeks" and a
 *    line that says why;
 *  - back to Last year: by week; the range stays as it is.
 */
export function applyCompare(view, cmp, rangeTouched = false) {
	if (!COMPARE.includes(cmp) || cmp === view.cmp) return { view, hint: null }
	if (cmp === 'ly') return { view: { ...view, cmp, by: 'week' }, hint: null }

	const next = { ...view, cmp, by: 'day' }
	if (!rangeTouched && view.range === DEFAULT_RANGE) {
		Object.assign(next, { range: 'next4w', from: null, to: null })
		const unit = cmp === 'lw' ? 'week' : 'day'
		return { view: next, hint: `Now “${RANGES.next4w.label}”: pick-ups that have gone by do not change from one ${unit} to the next.` }
	}
	return { view: next, hint: null }
}
